import { useMemo, useState } from 'react';
import { FiPlus } from 'react-icons/fi';

import { Button, ButtonOutlined } from '@/components/Button';
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal';
import { Autocomplete, Input } from '@/components/Inputs';
import Modal from '@/components/Modal/Modal';
import { PERMISSIONS } from '@/permissions/permissions.catalog';
import { usePermissions } from '@/permissions/usePermissions';
import { ALL_EQUIPMENT_LIMIT } from '@/store/equipment/equipment.api';
import {
  useAllEquipmentQuery,
  useCreateEquipmentMutation,
  useDeleteEquipmentMutation,
  useEquipmentCategoriesQuery,
  useEquipmentTypesQuery,
} from '@/store/equipment/equipment.store';
import { toast } from '@/utils/toast';

import styles from './modals/TransactionModal.module.scss';

/**
 * The catalog category an equipment added from an expense form is filed under. Vessel
 * seeds the categories globally and matches them by name, so the name is the only handle
 * the frontend has on it.
 */
export const EXPENSE_EQUIPMENT_CATEGORY = 'Additional Equipment';

interface ExpenseEquipmentFieldProps {
  /** The picked equipment's id, '' when none. */
  value: string;
  onChange(id: string): void;
  /** Off while the form behind it is closed, so a mounted-but-hidden modal asks for nothing. */
  enabled?: boolean;
  /** The picker's box only — width, flex, grid placement. */
  className?: string;
}

/**
 * The equipment picker of the transaction, invoice and quote forms. Besides picking, it
 * adds a stub by name alone — filed under Additional Equipment, the rest of its fields
 * filled later from the equipment's own page — and removes the stubs that live there.
 * Both are behind the Equipment right; without it the picker only picks.
 */
export function ExpenseEquipmentField({
  value,
  onChange,
  enabled = true,
  className,
}: ExpenseEquipmentFieldProps) {
  const { can } = usePermissions();
  const canManageEquipment = can(PERMISSIONS.vessel.Equipment);

  const [search, setSearch] = useState('');
  /** Non-null while the add dialog is up; the string seeds its name field. */
  const [newName, setNewName] = useState<string | null>(null);
  const [removing, setRemoving] = useState<{ id: string; name: string } | null>(null);
  /** The item just added, named until the refetched list carries it — without it the
   *  picker shows the empty search in between, and the modal hands focus back to it. */
  const [created, setCreated] = useState<{ id: string; name: string } | null>(null);

  const { data: equipment = [] } = useAllEquipmentQuery(enabled);
  const { data: categories } = useEquipmentCategoriesQuery(
    ALL_EQUIPMENT_LIMIT,
    '',
    enabled && canManageEquipment,
  );
  const categoryId = useMemo(
    () =>
      categories?.pages
        .flatMap((page) => page.items)
        .find(({ name }) => name.toLowerCase() === EXPENSE_EQUIPMENT_CATEGORY.toLowerCase())?.id ??
      '',
    [categories],
  );
  // Vessel requires a type on every create and the category has no generic one, so a
  // name-only add takes the category's first type rather than asking for one.
  const { data: types } = useEquipmentTypesQuery(categoryId);
  const defaultTypeId = types?.items[0]?.id ?? '';
  const canAdd = canManageEquipment && !!defaultTypeId;

  const { mutateAsync: createEquipment, isPending: isCreating } = useCreateEquipmentMutation();
  const { mutateAsync: deleteEquipment } = useDeleteEquipmentMutation();

  const options = equipment
    .filter(({ name }) => name.toLowerCase().includes(search.toLowerCase()))
    .map(({ id, name, type }) => ({
      label: name,
      value: id,
      ...(canManageEquipment &&
        type.categoryId === categoryId && {
          onRemove: () => setRemoving({ id, name }),
          removeLabel: `Delete ${name}`,
        }),
    }));

  const handleCreate = async (name: string) => {
    try {
      const { id } = await createEquipment({
        typeId: defaultTypeId,
        name,
        installed: null,
        runningHours: null,
        fields: {},
      });
      toast.success('Equipment added');
      setCreated({ id, name });
      onChange(id);
      setSearch('');
      setNewName(null);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Couldn’t add the equipment. Please try again.',
      );
    }
  };

  const handleRemove = async ({ id }: { id: string }) => {
    try {
      await deleteEquipment(id);
      if (id === value) onChange('');
      toast.success('Equipment deleted');
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Couldn’t delete the equipment. Please try again.',
      );
    }
  };

  return (
    <>
      <Autocomplete
        className={className}
        label="Equipment"
        placeholder="Search equipment"
        value={search}
        onChange={setSearch}
        options={options}
        onItemSelect={(id) => {
          onChange(id ?? '');
          if (id) setSearch('');
        }}
        selectedOptionLabel={
          equipment.find(({ id }) => id === value)?.name ??
          (created?.id === value ? created.name : undefined)
        }
        onNewOption={canAdd ? (typed) => setNewName(typed.trim()) : undefined}
        alwaysOfferNewOption={canAdd}
        newOptionLabel={(typed) => (
          <>
            <FiPlus size={14} aria-hidden="true" />
            {typed ? `Add “${typed}”` : 'Add Equipment'}
          </>
        )}
      />

      <NewEquipmentModal
        open={newName !== null}
        initialName={newName ?? ''}
        saving={isCreating}
        onClose={() => setNewName(null)}
        onSave={handleCreate}
      />

      <ConfirmationModal
        open={removing !== null}
        onClose={() => setRemoving(null)}
        onConfirm={() => {
          if (removing) void handleRemove(removing);
        }}
        title="Delete Equipment"
        message={`Are you sure you want to delete “${removing?.name ?? 'this equipment'}”? It’ll also be removed from the vessel’s equipment.`}
      />
    </>
  );
}

interface NewEquipmentModalProps {
  open: boolean;
  initialName: string;
  saving: boolean;
  onClose(): void;
  onSave(name: string): void | Promise<void>;
}

function NewEquipmentModal({ open, onClose, ...form }: NewEquipmentModalProps) {
  return (
    <Modal open={open} onClose={onClose} title="Add Equipment">
      <NewEquipmentForm onClose={onClose} {...form} />
    </Modal>
  );
}

/** Mounted fresh on every open (the modal unmounts its children when closed), so the
 *  name starts from what was typed into the picker each time. */
function NewEquipmentForm({
  initialName,
  saving,
  onClose,
  onSave,
}: Omit<NewEquipmentModalProps, 'open'>) {
  const [name, setName] = useState(initialName);
  const [error, setError] = useState('');

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        // The dialog sits over another form; its submit must not reach that one.
        e.preventDefault();
        e.stopPropagation();
        const trimmed = name.trim();
        if (!trimmed) {
          setError('Please enter a name.');
          return;
        }
        void onSave(trimmed);
      }}
    >
      <Input
        label="Name"
        placeholder="Port main engine"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          if (error) setError('');
        }}
        error={error}
        mandatory
        autoFocus
      />
      <div className={styles.buttons}>
        <ButtonOutlined type="button" onClick={onClose}>
          Cancel
        </ButtonOutlined>
        <Button type="submit" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
